import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { getUserAuthoredPapers } from '@/lib/api/paper-queries/getUserAuthoredPapers';
import { Paper } from '@/types/paper';

interface MyPapersData {
  publicPapers: Paper[];
  privatePapers: Paper[];
  totalViews: number;
  totalReviews: number;
  totalComments: number;
  isLoading: boolean;
  error: Error | null;
}

export const useMyPapers = (): MyPapersData => {
  const { user } = useAuth();

  const { data, isLoading, error } = useQuery({
    queryKey: ['my-papers', user?.id],
    queryFn: async () => {
      if (!user?.id) {
        return { publicPapers: [], privatePapers: [] };
      }
      return getUserAuthoredPapers(user.id);
    },
    enabled: !!user?.id,
    staleTime: 2 * 60 * 1000, // 2 minutes
    refetchOnWindowFocus: true,
  });

  const publicPapers = data?.publicPapers || [];
  const privatePapers = data?.privatePapers || [];
  const allPapers = [...publicPapers, ...privatePapers];

  // Calculate aggregate stats
  const totalViews = allPapers.reduce((sum, paper) => sum + (paper.views || 0), 0);
  const totalReviews = allPapers.reduce((sum, paper) => sum + ((paper as any).reviewCount || 0), 0);
  const totalComments = allPapers.reduce((sum, paper) => sum + ((paper as any).commentCount || 0), 0);

  return {
    publicPapers,
    privatePapers,
    totalViews,
    totalReviews,
    totalComments,
    isLoading,
    error: error as Error | null,
  };
};
